import { useState } from "react";
import {
  Search,
  Loader2,
  ExternalLink,
  TrendingDown,
  Globe,
  BarChart3,
  Star,
  ShoppingCart,
  Eye,
  ArrowRightLeft,
  Bookmark,
} from "lucide-react";
import { Input } from "../components/ui/input";
import WatchlistButton from "../components/WatchlistButton";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUGGESTIONS = [
  "iPhone 15 Pro",
  "Nike Air Jordan 1",
  "PS5 Slim",
  "AirPods Pro 2",
  "MacBook Air M3",
  "Dyson V15",
  "Samsung Galaxy S24",
  "Nintendo Switch OLED",
];

const FLAG_MAP = {
  France: "\u{1F1EB}\u{1F1F7}",
  USA: "\u{1F1FA}\u{1F1F8}",
  Allemagne: "\u{1F1E9}\u{1F1EA}",
  UK: "\u{1F1EC}\u{1F1E7}",
  Chine: "\u{1F1E8}\u{1F1F3}",
};

const BAR_COLORS = ["#00E676", "#FFCC00", "#00BCD4", "#FF6B35", "#E040FB"];

const RATES = {
  EUR: { EUR: 1, USD: 1.08, GBP: 0.86, HKD: 8.44 },
  USD: { EUR: 0.93, USD: 1, GBP: 0.79, HKD: 7.82 },
  GBP: { EUR: 1.16, USD: 1.27, GBP: 1, HKD: 9.84 },
  HKD: { EUR: 0.12, USD: 0.13, GBP: 0.10, HKD: 1 },
};

const CURRENCY_LABELS = { EUR: "\u20AC Euro", USD: "$ Dollar US", GBP: "\u00A3 Livre Sterling", HKD: "HK$ Dollar HK" };

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-[#0A0A0A] border-2 border-[#00E676] p-3 text-white text-sm">
      <p className="font-bold">
        {d.flag} {d.name}
      </p>
      <p className="text-[#00E676] font-extrabold text-lg">
        {d.symbol}
        {d.value?.toFixed(2)}
      </p>
      <p className="text-white/50 text-xs">
        {d.count} vendeur{d.count > 1 ? "s" : ""}
      </p>
    </div>
  );
}

export default function Comparateur() {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [followingIds, setFollowingIds] = useState(new Set());

  // Currency converter state
  const [convFrom, setConvFrom] = useState("EUR");
  const [convTo, setConvTo] = useState("USD");
  const [convAmount, setConvAmount] = useState("");

  const handleCompare = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/compare`, { query: query.trim() });
      setResult(res.data);
      if (res.data.total_products > 0) {
        toast.success(
          `${res.data.total_products} produits compares sur 5 marches !`
        );
      } else {
        toast.info("Aucun resultat trouve.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la comparaison");
    } finally {
      setLoading(false);
    }
  };

  const quickSearch = (q) => {
    setQuery(q);
    setTimeout(
      () => document.getElementById("compare-form")?.requestSubmit(),
      100
    );
  };

  const handleFollow = async (product, market) => {
    const key = `${product.title}-${market.label}`;
    if (followingIds.has(key)) return;
    try {
      await axios.post(`${API}/watchlist`, {
        product_name: product.title || product.seller,
        product_image: product.image || "",
        search_query: result?.query || query,
        buy_platform: `Google Shopping ${market.label}`,
        buy_price: product.price,
        sell_platform: "",
        sell_price: 0,
        margin: 0,
        margin_percent: 0,
        buy_url: product.url || "",
        sell_url: "",
        notes: `Via Comparateur - ${market.flag} ${market.label}`,
      });
      setFollowingIds((prev) => new Set([...prev, key]));
      toast.success(`${(product.title || product.seller || "Produit").slice(0, 40)} ajouté à la watchlist`);
    } catch {
      toast.error("Erreur lors de l'ajout");
    }
  };

  const chartData =
    result?.markets
      ?.filter((m) => m.best_price !== null)
      .map((m) => ({
        name: m.label,
        value: m.best_price,
        avg: m.avg_price,
        symbol: m.symbol,
        flag: m.flag,
        count: m.count,
      }))
      .sort((a, b) => a.value - b.value) || [];

  const bestMarket = result?.markets?.find(
    (m) => m.label === result.best_market
  );

  const converted =
    convAmount && RATES[convFrom] && RATES[convFrom][convTo]
      ? (parseFloat(convAmount) * RATES[convFrom][convTo]).toFixed(2)
      : "";

  return (
    <div className="space-y-6" data-testid="comparateur-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight flex items-center gap-3">
          <BarChart3 className="text-[#00E676]" size={36} />
          COMPARATEUR
        </h1>
        <p className="text-muted-foreground mt-2 uppercase tracking-wider text-xs sm:text-sm">
          Comparez les prix d'un produit sur 5 marches en temps reel
        </p>
      </div>

      {/* Search */}
      <form
        id="compare-form"
        onSubmit={handleCompare}
        className="neo-card p-4 sm:p-6"
      >
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <div className="flex-1 relative">
            <Input
              type="text"
              placeholder="Nom du produit (ex: iPhone 15 Pro, PS5 Slim...)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="neo-input w-full pr-12"
              data-testid="compare-input"
            />
            <Globe
              className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground"
              size={20}
            />
          </div>
          <button
            type="submit"
            disabled={loading || !query.trim()}
            className="neo-button-primary inline-flex items-center justify-center gap-2 min-w-[180px]"
            data-testid="compare-submit"
          >
            {loading ? (
              <>
                <Loader2 size={20} className="animate-spin" /> Comparaison...
              </>
            ) : (
              <>
                <Search size={20} /> Comparer 5 marches
              </>
            )}
          </button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          {"\u{1F1EB}\u{1F1F7}"} France {"\u00B7"} {"\u{1F1FA}\u{1F1F8}"} USA {"\u00B7"} {"\u{1F1E9}\u{1F1EA}"} Allemagne {"\u00B7"} {"\u{1F1EC}\u{1F1E7}"} UK {"\u00B7"} {"\u{1F1E8}\u{1F1F3}"} Chine {"\u2014"} Scan simultane en temps reel
        </p>
      </form>

      {/* Suggestions */}
      {!result && !loading && (
        <div
          className="flex flex-wrap gap-2"
          data-testid="compare-suggestions"
        >
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              onClick={() => quickSearch(s)}
              className="neo-button-secondary text-xs sm:text-sm px-3 py-2"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="neo-card p-12 text-center">
          <Loader2
            className="animate-spin text-[#00E676] mx-auto mb-4"
            size={48}
          />
          <p className="font-bold text-lg">Scan de 5 marches en cours...</p>
          <p className="text-muted-foreground text-sm mt-1">
            {"\u{1F1EB}\u{1F1F7}"} {"\u{1F1FA}\u{1F1F8}"} {"\u{1F1E9}\u{1F1EA}"} {"\u{1F1EC}\u{1F1E7}"} {"\u{1F1E8}\u{1F1F3}"}
          </p>
        </div>
      )}

      {/* Results */}
      {result && !loading && (
        <>
          {/* Summary Card */}
          {result.best_market && (
            <div
              className="neo-card p-4 sm:p-6 border-[#00E676]"
              data-testid="compare-summary"
            >
              <div className="flex flex-col md:flex-row gap-6 items-start">
                {result.product_image && (
                  <img
                    src={result.product_image}
                    alt={result.query}
                    className="w-24 h-24 object-contain border-2 border-[#0A0A0A] flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold mb-1">
                    Meilleur prix trouve
                  </p>
                  <h2 className="text-xl sm:text-2xl font-extrabold truncate">
                    {result.query}
                  </h2>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-3xl sm:text-4xl font-extrabold text-[#00E676] drop-shadow-[0_0_10px_rgba(0,230,118,0.3)]">
                      {result.best_currency}
                      {result.best_price?.toFixed(2)}
                    </span>
                    <span className="text-lg">
                      {FLAG_MAP[result.best_market]} {result.best_market}
                    </span>
                  </div>
                  {result.savings && (
                    <div className="mt-3 inline-flex items-center gap-2 bg-[#00E676]/10 border border-[#00E676]/40 px-3 py-1.5">
                      <TrendingDown size={16} className="text-[#00E676]" />
                      <span className="font-bold text-sm text-[#00E676]">
                        Economie : {(result.savings || 0).toFixed(2)} vs le plus cher (
                        {result.savings_pct}%)
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-center gap-3 flex-shrink-0">
                  <div className="text-center">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold">
                      Produits scannes
                    </p>
                    <p className="text-3xl font-extrabold">
                      {result.total_products}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      sur 5 marches
                    </p>
                  </div>
                  {bestMarket?.cheapest?.url && (
                    <div className="flex flex-col gap-2">
                      <a
                        href={bestMarket.cheapest.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-[#00E676] text-[#0A0A0A] font-bold uppercase tracking-wider px-5 py-2.5 text-sm hover:shadow-[0_0_20px_rgba(0,230,118,0.4)] transition-all border-2 border-[#0A0A0A] shadow-[3px_3px_0px_#0A0A0A]"
                        data-testid="compare-view-product-btn"
                      >
                        <Eye size={16} />
                        Voir le produit
                      </a>
                      <button
                        onClick={() => handleFollow(bestMarket.cheapest, bestMarket)}
                        disabled={followingIds.has(`${bestMarket.cheapest.title}-${bestMarket.label}`)}
                        className={`inline-flex items-center justify-center gap-2 font-bold uppercase tracking-wider px-5 py-2.5 text-sm border-2 border-[#0A0A0A] shadow-[3px_3px_0px_#0A0A0A] transition-all ${followingIds.has(`${bestMarket.cheapest.title}-${bestMarket.label}`) ? "bg-[#FFD700] text-[#0A0A0A]" : "bg-transparent hover:bg-[#FFD700]/10"}`}
                        data-testid="compare-follow-best-btn"
                      >
                        <Bookmark size={16} className={followingIds.has(`${bestMarket.cheapest.title}-${bestMarket.label}`) ? "fill-[#0A0A0A]" : ""} />
                        {followingIds.has(`${bestMarket.cheapest.title}-${bestMarket.label}`) ? "Suivi" : "Suivre"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Chart */}
          {chartData.length > 0 && (
            <div className="neo-card p-4 sm:p-6" data-testid="compare-chart">
              <h3 className="font-bold uppercase tracking-wider text-sm mb-4 flex items-center gap-2">
                <BarChart3 size={18} className="text-[#00E676]" />
                Comparaison des meilleurs prix par marche
              </h3>
              <div className="h-[300px] sm:h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid
                      strokeDasharray="none"
                      stroke="#0A0A0A"
                      strokeWidth={1}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="name"
                      tick={{ fill: "#0A0A0A", fontWeight: 700, fontSize: 13 }}
                      tickLine={{ stroke: "#0A0A0A" }}
                      axisLine={{ stroke: "#0A0A0A", strokeWidth: 2 }}
                      tickFormatter={(v, i) =>
                        `${chartData[i]?.flag || ""} ${v}`
                      }
                    />
                    <YAxis
                      tick={{ fill: "#0A0A0A", fontWeight: 600, fontSize: 12 }}
                      tickLine={{ stroke: "#0A0A0A" }}
                      axisLine={{ stroke: "#0A0A0A", strokeWidth: 2 }}
                    />
                    <Tooltip
                      content={<CustomTooltip />}
                      cursor={{ fill: "rgba(0,230,118,0.05)" }}
                    />
                    <Bar
                      dataKey="value"
                      radius={[0, 0, 0, 0]}
                      stroke="#0A0A0A"
                      strokeWidth={2}
                    >
                      {chartData.map((_, idx) => (
                        <Cell
                          key={idx}
                          fill={BAR_COLORS[idx % BAR_COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Market Cards — show ALL 5, even if 0 results */}
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
            data-testid="compare-markets"
          >
            {result.markets
              .sort((a, b) => (a.best_price || Infinity) - (b.best_price || Infinity))
              .map((market) => {
                const isBest = market.label === result.best_market;
                return (
                  <div
                    key={market.code}
                    className={`neo-card p-4 sm:p-5 ${isBest ? "border-[#00E676] bg-[#00E676]/5 dark:bg-[#00E676]/10" : ""}`}
                    data-testid={`compare-market-${market.code}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{market.flag}</span>
                        <div>
                          <p className="font-bold text-sm">{market.label}</p>
                          <p className="text-[10px] text-muted-foreground uppercase">
                            {market.count} vendeur
                            {market.count !== 1 ? "s" : ""}
                          </p>
                        </div>
                      </div>
                      {isBest && (
                        <span className="bg-[#00E676] text-[#0A0A0A] px-2 py-1 text-[10px] font-extrabold uppercase tracking-wider border-2 border-[#0A0A0A] shadow-[2px_2px_0px_#0A0A0A]">
                          Meilleur prix
                        </span>
                      )}
                    </div>

                    {market.count > 0 ? (
                      <>
                        <div className="flex items-baseline gap-2 mb-2">
                          <span
                            className={`text-2xl font-extrabold ${isBest ? "text-[#00E676]" : ""}`}
                          >
                            {market.symbol}
                            {market.best_price?.toFixed(2)}
                          </span>
                          {market.avg_price &&
                            market.avg_price !== market.best_price && (
                              <span className="text-sm text-muted-foreground">
                                moy. {market.symbol}
                                {market.avg_price?.toFixed(2)}
                              </span>
                            )}
                        </div>

                        {/* Cheapest product highlight with link + follow */}
                        {(() => {
                          const cheapest = [...(market.products || [])].sort((a, b) => (a.price || 999999) - (b.price || 999999))[0];
                          if (!cheapest) return null;
                          return (
                            <div className="bg-[#0A0A0A]/5 dark:bg-[#00E676]/5 border border-dashed border-[#00E676]/30 p-2 mb-3 flex items-center justify-between gap-2" data-testid={`cheapest-${market.code}`}>
                              <div className="min-w-0 flex-1">
                                <p className="text-[10px] text-[#00E676] font-bold uppercase">Moins cher</p>
                                <p className="text-xs font-medium truncate">{cheapest.seller || cheapest.title || "Vendeur"} — {market.symbol}{(cheapest.price || 0).toFixed(2)}</p>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <WatchlistButton product={{ product_name: cheapest.title || result.query, search_query: result.query, product_image: cheapest.image, buy_platform: market.label, buy_price: cheapest.price, buy_url: cheapest.url }} className="text-[10px] px-2 py-0.5" />
                                {cheapest.url && (
                                  <a href={cheapest.url} target="_blank" rel="noopener noreferrer" className="neo-button-secondary text-[10px] px-2 py-0.5 flex items-center gap-1">
                                    <ExternalLink size={10} /> Voir
                                  </a>
                                )}
                              </div>
                            </div>
                          );
                        })()}

                        {/* Top 5 sellers — sorted cheapest first */}
                        <div className="space-y-1.5">
                          {[...(market.products || [])].sort((a, b) => (a.price || 999999) - (b.price || 999999)).slice(0, 5).map((p, pIdx) => {
                            const followKey = `${p.title}-${market.label}`;
                            const isFollowed = followingIds.has(followKey);
                            return (
                              <div
                                key={pIdx}
                                className="flex items-center justify-between gap-2 py-1.5 border-t border-[#0A0A0A]/10 dark:border-[#00E676]/10"
                              >
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  {p.image && (
                                    <img
                                      src={p.image}
                                      alt=""
                                      className="w-8 h-8 object-contain flex-shrink-0 border border-[#0A0A0A]/10"
                                    />
                                  )}
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-1.5 mb-0.5">
                                      <p className="text-xs font-bold truncate">
                                        {p.seller || "Vendeur"}
                                      </p>
                                      <span className={`text-[9px] px-1 py-0.5 font-bold uppercase border flex-shrink-0 leading-none ${p.condition === "NEW" ? "border-[#00E676]/50 text-[#00E676]" : p.condition === "REFURBISHED" ? "border-[#FFCC00]/50 text-[#FFCC00]" : "border-[#FF6B35]/50 text-[#FF6B35]"}`} data-testid={`condition-badge-${market.code}-${pIdx}`}>
                                        {p.condition === "NEW" ? "Neuf" : p.condition === "REFURBISHED" ? "Recond." : "Occasion"}
                                      </span>
                                    </div>
                                    {p.rating && (
                                      <div className="flex items-center gap-0.5">
                                        <Star
                                          size={8}
                                          className="text-yellow-500 fill-yellow-500"
                                        />
                                        <span className="text-[10px] text-muted-foreground">
                                          {p.rating}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                  <span className="font-bold text-sm">
                                    {market.symbol}
                                    {p.price?.toFixed(2)}
                                  </span>
                                  <button
                                    onClick={() => handleFollow(p, market)}
                                    disabled={isFollowed}
                                    className={`p-1 transition-colors ${isFollowed ? "text-[#FFD700]" : "text-muted-foreground hover:text-[#FFD700]"}`}
                                    title={isFollowed ? "Suivi" : "Suivre"}
                                    data-testid={`follow-btn-${market.code}-${pIdx}`}
                                  >
                                    <Bookmark
                                      size={14}
                                      className={isFollowed ? "fill-[#FFD700]" : ""}
                                    />
                                  </button>
                                  {p.url && (
                                    <a
                                      href={p.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-[#00E676] hover:underline"
                                    >
                                      <ExternalLink size={12} />
                                    </a>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground py-4 text-center">
                        Aucun resultat pour ce marche
                      </p>
                    )}
                  </div>
                );
              })}
          </div>

          {/* No results at all */}
          {result.total_products === 0 && (
            <div className="neo-card p-12 text-center">
              <ShoppingCart
                className="mx-auto mb-4 text-muted-foreground"
                size={48}
              />
              <p className="text-lg font-bold">Aucun resultat trouve</p>
              <p className="text-muted-foreground mt-2">
                Essayez un autre produit
              </p>
            </div>
          )}
        </>
      )}

      {/* Currency Converter */}
      <div className="neo-card p-4 sm:p-6" data-testid="currency-converter">
        <h3 className="font-bold uppercase tracking-wider text-sm mb-4 flex items-center gap-2">
          <ArrowRightLeft size={18} className="text-[#00BCD4]" />
          Convertisseur de devises
        </h3>
        <div className="flex flex-col sm:flex-row gap-3 items-end">
          <div className="flex-1">
            <label className="text-xs uppercase tracking-wider text-muted-foreground font-bold block mb-1">
              Montant
            </label>
            <Input
              type="number"
              placeholder="100.00"
              value={convAmount}
              onChange={(e) => setConvAmount(e.target.value)}
              className="neo-input w-full"
              data-testid="conv-amount"
              min={0}
              step="0.01"
            />
          </div>
          <div className="flex-1">
            <label className="text-xs uppercase tracking-wider text-muted-foreground font-bold block mb-1">
              De
            </label>
            <select
              value={convFrom}
              onChange={(e) => setConvFrom(e.target.value)}
              className="neo-input w-full h-10 px-3 border-2 border-[#0A0A0A] bg-background"
              data-testid="conv-from"
            >
              {Object.entries(CURRENCY_LABELS).map(([code, label]) => (
                <option key={code} value={code}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={() => {
              setConvFrom(convTo);
              setConvTo(convFrom);
            }}
            className="p-2 border-2 border-[#0A0A0A] hover:bg-[#00BCD4]/10 transition-colors flex-shrink-0 self-end"
            data-testid="conv-swap"
            type="button"
          >
            <ArrowRightLeft size={18} />
          </button>
          <div className="flex-1">
            <label className="text-xs uppercase tracking-wider text-muted-foreground font-bold block mb-1">
              Vers
            </label>
            <select
              value={convTo}
              onChange={(e) => setConvTo(e.target.value)}
              className="neo-input w-full h-10 px-3 border-2 border-[#0A0A0A] bg-background"
              data-testid="conv-to"
            >
              {Object.entries(CURRENCY_LABELS).map(([code, label]) => (
                <option key={code} value={code}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="text-xs uppercase tracking-wider text-muted-foreground font-bold block mb-1">
              Resultat
            </label>
            <div className="neo-input w-full h-10 flex items-center px-3 border-2 border-[#00BCD4] bg-[#00BCD4]/5 font-bold text-lg" data-testid="conv-result">
              {converted
                ? `${Object.keys(RATES[convTo] || {})[0] === convTo ? "" : ""}${converted} ${convTo}`
                : "\u2014"}
            </div>
          </div>
        </div>
        <p className="text-[10px] text-muted-foreground mt-2">
          Taux indicatifs. {"\u{1F1EB}\u{1F1F7}"} EUR {"\u00B7"} {"\u{1F1FA}\u{1F1F8}"} USD {"\u00B7"} {"\u{1F1EC}\u{1F1E7}"} GBP {"\u00B7"} {"\u{1F1ED}\u{1F1F0}"} HKD
        </p>
      </div>

      {/* Empty state */}
      {!result && !loading && (
        <div
          className="neo-card p-8 sm:p-12 text-center"
          data-testid="compare-empty"
        >
          <Globe className="mx-auto mb-4 text-muted-foreground" size={48} />
          <p className="text-lg sm:text-xl font-bold text-muted-foreground">
            Comparez les prix
          </p>
          <p className="text-muted-foreground mt-2 max-w-md mx-auto text-sm">
            Entrez un produit et comparez instantanement les prix sur 5 marches
            internationaux
          </p>
        </div>
      )}
    </div>
  );
}
